import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Page } from '../models/page-wrapper.model';
import { AppUser } from './user.model';
import { PickerRecentCacheService } from '../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = `${environment.restApiUrl}users`; // e.g., http://localhost:8080/api/users
  private readonly pickerEntityKey = 'user';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  // Corresponds to GET /api/users
  // getAllUsers(params?: any): Observable<Page<AppUser>> {
  //   return this.http.get<Page<AppUser>>(this.apiUrl, { params: params }).pipe(
  //     catchError(this.handleError)
  //   );
  // }

  getAllUsers(params?: any): Observable<Page<AppUser>> {
    return this.http.get<Page<AppUser>>(this.apiUrl + '/search', { params: params }).pipe(
      catchError(this.handleError)
    );
  }

  searchUsers(query: string, page = 0, size = 25): Observable<Page<AppUser>> {
    return forkJoin([
      this.searchUsersByField('fullName', query, page, size),
      this.searchUsersByField('email', query, page, size),
    ]).pipe(
      map(([fullNamePage, emailPage]) => this.mergeUserPages(fullNamePage, emailPage, page, size)),
      catchError(this.handleError)
    );
  }

  loadRecentUsers(limit = 10): Observable<AppUser[]> {
    return from(this.recentPickerCache.loadRecent<AppUser>(this.pickerEntityKey, limit));
  }

  getFreshUserById(id: number | string): Observable<AppUser> {
    return this.getUserById(Number(id));
  }

  rememberUser(user: AppUser): Observable<void> {
    const record: PickerRecentRecord<AppUser> = {
      key: `${this.pickerEntityKey}::${user.id}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: user.id ?? 0,
      label: `${user.fullName ?? ''}${user.email ? ` - ${user.email}` : ''}`.trim(),
      payload: user,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createUserPickerAdapter(): EntityDropdownPickerAdapter<AppUser> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentUsers(limit),
      search: (query: string, page: number, size: number) => this.searchUsers(query, page, size),
      getFreshById: (id: number | string) => this.getFreshUserById(id),
      rememberSelection: (user: AppUser) => this.rememberUser(user),
      getId: (user: AppUser) => user.id ?? 0,
      getLabel: (user: AppUser) => `${user.fullName ?? user.email ?? user.id ?? ''}${user.email && user.fullName ? ` - ${user.email}` : ''}`.trim(),
      getSecondaryLabel: (user: AppUser) => user.mobileNumber ? `${user.mobileNumber}` : '',
      filterBy: 'fullName,email',
    };
  }


  // Corresponds to GET /api/users/{id}
  getUserById(id: number): Observable<AppUser> {
    return this.http.get<AppUser>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  // Corresponds to GET /api/users/{id}
  getUserByFirebaseId(firebaseId: string): Observable<AppUser> {
    return this.http.get<AppUser>(`${this.apiUrl}/firebase/${firebaseId}`).pipe(
      catchError(this.handleError)
    );
  }

  // Corresponds to GET /api/users/{id}
  getUserByEmailAddress(emailAddress: string): Observable<AppUser> {
    return this.http.get<AppUser>(`${this.apiUrl}/email/${emailAddress}`).pipe(
      catchError(this.handleError)
    );
  }

  // Corresponds to GET /api/users/{id}
  updateUser(user: AppUser): Observable<AppUser> {
    return this.http.put<AppUser>(`${this.apiUrl}/${user.id}`, user).pipe(
      catchError(this.handleError)
    );
  }

  // Corresponds to POST /api/users
  createUser(user: AppUser): Observable<AppUser> {
    return this.http.post<AppUser>(this.apiUrl, user).pipe(
      catchError(this.handleError)
    );
  }
  
  // You can add other methods for createUser, updateUser, deleteUser, getUserByFirebaseId as needed.

  private handleError(error: any): Observable<never> {
    console.error('An error occurred in UserService:', error);
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchUsersByField(
    field: 'fullName' | 'email',
    query: string,
    page: number,
    size: number
  ): Observable<Page<AppUser>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<AppUser>>(this.apiUrl + '/search', { params }).pipe(
      catchError(() => of(this.emptyUserPage(page, size)))
    );
  }

  private mergeUserPages(
    firstPage: Page<AppUser>,
    secondPage: Page<AppUser>,
    page: number,
    size: number
  ): Page<AppUser> {
    const merged = new Map<number, AppUser>();
    [...(firstPage.content || []), ...(secondPage.content || [])].forEach((user) => {
      if (user?.id !== undefined && user?.id !== null) {
        merged.set(user.id, user);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);

    return {
      ...firstPage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptyUserPage(page: number, size: number): Page<AppUser> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }
}
